import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bookmark, Search, Trash2, Bell, BellOff, ExternalLink, Loader2, Plus, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  contractor: { label: 'Yüklenici', color: 'bg-green-100 text-green-700', path: '/find-contractors' },
  supplier: { label: 'Tedarikçi', color: 'bg-purple-100 text-purple-700', path: '/find-suppliers' },
  exhibitor: { label: 'Katılımcı', color: 'bg-blue-100 text-blue-700', path: '/' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const filterSummary = (filters) => {
  const parts = [];
  if (filters.q) parts.push(`"${filters.q}"`);
  if (filters.city) parts.push(filters.city);
  if (filters.service) parts.push(filters.service);
  if (filters.category) parts.push(filters.category);
  if (filters.fair) parts.push(filters.fair);
  return parts.length ? parts.join(' · ') : 'Tüm sonuçlar';
};

export default function SavedSearchesPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [newCounts, setNewCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        axios.get(`${API}/saved-searches`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/saved-searches/new-counts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { counts: {} } })),
      ]);
      setSearches(listRes.data.items || []);
      setNewCounts(countRes.data.counts || {});
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/saved-searches/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearches(prev => prev.filter(s => s.id !== id));
      toast.success('Arama silindi');
    } catch { toast.error('Silinemedi'); }
    finally { setDeletingId(null); }
  };

  const toggleAlert = async (s) => {
    setTogglingId(s.id);
    try {
      await axios.patch(`${API}/saved-searches/${s.id}`,
        { email_alerts: !s.email_alerts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearches(prev => prev.map(x => x.id === s.id ? { ...x, email_alerts: !x.email_alerts } : x));
      toast.success(s.email_alerts ? 'E-posta bildirimi kapatıldı' : 'E-posta bildirimi açıldı');
    } catch { toast.error('Güncellenemedi'); }
    finally { setTogglingId(null); }
  };

  const runSearch = (s) => {
    const meta = TYPE_LABELS[s.search_type] || TYPE_LABELS.contractor;
    const params = new URLSearchParams();
    const f = s.filters || {};
    if (f.q) params.set('q', f.q);
    if (f.city) params.set('city', f.city);
    if (f.service) params.set('service', f.service);
    if (f.category) params.set('category', f.category);
    if (f.fair) params.set('fair', f.fair);
    navigate(`${meta.path}?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <Bookmark className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Kayıtlı Aramalar</h1>
              <p className="text-sm text-gray-500">Kaydettiğiniz filtreli aramaları buradan çalıştırın</p>
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            Geri
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : searches.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Search className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-700 font-semibold mb-1">Kayıtlı arama yok</p>
            <p className="text-sm text-gray-400 mb-4">Yüklenici veya tedarikçi ararken filtreleri kaydedebilirsiniz.</p>
            <button
              onClick={() => navigate('/find-contractors')}
              className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />Arama Yap
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {searches.map(s => {
              const meta = TYPE_LABELS[s.search_type] || TYPE_LABELS.contractor;
              return (
                <div
                  key={s.id}
                  data-testid={`saved-search-${s.id}`}
                  className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start gap-4 hover:shadow-sm transition-shadow"
                >
                  <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                    <Search className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-gray-900 text-sm truncate">{s.name}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${meta.color}`}>
                        {meta.label}
                      </span>
                      {s.email_alerts && (
                        <span className="text-xs bg-amber-50 text-amber-600 font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Bell className="w-2.5 h-2.5" />Bildirim açık
                        </span>
                      )}
                      {newCounts[s.id] > 0 && (
                        <span className="text-xs bg-green-50 text-green-700 font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 border border-green-200">
                          <TrendingUp className="w-2.5 h-2.5" />+{newCounts[s.id]} son 7 günde
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{filterSummary(s.filters)}</p>
                    <p className="text-xs text-gray-400 mt-1">{fmtDate(s.created_at)} tarihinde kaydedildi</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => runSearch(s)}
                      title="Aramayı çalıştır"
                      className="p-2 rounded-xl text-blue-600 hover:bg-blue-50 transition-colors"
                      data-testid={`run-search-${s.id}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleAlert(s)}
                      disabled={togglingId === s.id}
                      title={s.email_alerts ? 'Bildirimi kapat' : 'E-posta bildirimi aç'}
                      className={`p-2 rounded-xl transition-colors ${s.email_alerts ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-400 hover:bg-gray-50'}`}
                      data-testid={`toggle-alert-${s.id}`}
                    >
                      {togglingId === s.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : s.email_alerts ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />
                      }
                    </button>
                    <button
                      onClick={() => handleDelete(s.id)}
                      disabled={deletingId === s.id}
                      title="Sil"
                      className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      data-testid={`delete-search-${s.id}`}
                    >
                      {deletingId === s.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Trash2 className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
